/**
 * Public `/docs/ai-setup`: partner-facing documentation for setting up
 * FinderlyFix with an AI coding agent (migration 070). Readable before signing
 * in, like `/legal/*`, so a developer can be sent the link.
 *
 * Describe only what is built: the MCP endpoint, its tools, the REST routes
 * an agent token can reach, and the human-only list. The tool names here are
 * the server's (routes/partner/mcp.ts); docs.test.tsx checks them.
 */
import { CodeBlock } from '../ui/kit';
import { AgentPicker } from '../agent/AgentPicker';
import { AgentPromptCard } from '../agent/NewcomerAgent';
import { InteractiveTerminalNote } from '../agent/AgentHandoff';
import { BUNDLE_FILES, BUNDLE_ZIP_NAME, RepoAgentConfig } from '../agent/RepoAgentConfig';
import { useOptionalSession } from '../auth/session';
import {
  AFTER_A_WINDOW,
  AGENT_SETUP_PATH,
  AGENT_WILL,
  HUMAN_ONLY,
  HUMAN_ONLY_FIELD_GROUPS,
  SETUP_PROMPT,
  TOKEN_ENV_VAR,
  WINDOW_NOTE,
  agentSetupUrl,
  curlExample,
  listInWords,
  mcpJsonConfig,
  mcpJsonOAuthConfig,
  mcpUrl,
  openApiUrl,
} from '../agent/handoff';

export const DOCUMENTED_TOOLS: Array<[string, string, 'read' | 'set up']> = [
  ['get_setup_status', 'What is done, what is left, and what needs a person, with portal links.', 'read'],
  ['accept_updated_terms', 'Show the link to an updated version of the Partner Terms and, when you tell it to, accept it for your organisation (it binds your organisation).', 'set up'],
  ['get_install_instructions', 'The snippet (live and test key), CSP sources and the ownership file.', 'read'],
  ['add_site_origin', 'Register the website’s origin (https://host).', 'set up'],
  ['verify_install', 'Tier 1: is the widget on the page. Tier 3: is the ownership file there.', 'set up'],
  ['verify_install_rendered', 'Tier 2: render the page in a real browser and check the widget mounts (waits up to 40 s).', 'set up'],
  ['get_verification_result', 'Recent verification results, or one by id (e.g. a render still in progress).', 'read'],
  ['get_install_health', 'Per website: installed, awaiting its first widget load, or erroring, with recent widget loads and rejections.', 'read'],
  ['wait_for_impression', 'Wait (up to 40 s) for the widget to report a new load after someone opens the page.', 'read'],
  ['get_widget_config', 'Read the widget settings: your newest draft by default, or the published version.', 'read'],
  ['get_widget_preview', 'A 15-minute link that shows the draft in the real widget, on the test key, before publishing.', 'read'],
  ['check_branding', 'Check a colour set against the contrast rules; suggests button text colour. The border colour is decorative: reported, never refused.', 'read'],
  ['update_widget_config', 'Save a draft of branding (including the border colour), copy and notices (including the subtitle), placement, scope (including the tab visitors land on), safety, consent and capture, routing, handoff or analytics settings. Every field is in its schema; an unknown one is refused, not dropped. Returns a preview link.', 'set up'],
  ['set_trades_and_area', 'Trades the widget handles and the ZIP prefixes you serve.', 'set up'],
  ['publish_widget_config', 'Publish the draft (and copy it to the test key).', 'set up'],
  ['list_contractors', 'Read the contractor roster.', 'read'],
  ['add_contractor', 'Add one contractor you named (optionally marked do-not-contact).', 'set up'],
  ['import_contractors_csv', 'Import a roster CSV (dry run first).', 'set up'],
  ['request_destination', 'Add a webhook or email destination; it is live at once, and owners and admins are emailed with a one-click Undo.', 'set up'],
  ['request_destination_change', 'Point a destination at a new URL or recipients; live at once, with an Undo for owners.', 'set up'],
  ['set_destination_status', 'Switch a destination off or back on (at once).', 'set up'],
  ['activate_contractors', 'Switch contractors on once you confirm that your organisation has bound them, in writing, to the contact rules; live at once, with an Undo for owners.', 'set up'],
  ['deactivate_contractors', 'Switch contractors off (at once).', 'set up'],
  ['record_contractor_reconsent', 'When a contractor who opted out tells you they want offers again: record how, when and what they said; their opt-out lifts at once, with an Undo for owners.', 'set up'],
  ['list_contractor_consent_events', 'The record of every contractor opt-out and re-consent: channel, date, evidence and who recorded it.', 'read'],
  ['request_escalation_contacts', 'Who is copied on lead notices; live at once, with an Undo for owners.', 'set up'],
  ['invite_team_member', 'Invite a teammate you name, in any role but owner. It waits for an owner or admin to approve it; then they are emailed an invitation (it says an AI agent sent it for your organisation) and join only by accepting it within 7 days. Not before an account an agent signed up is claimed.', 'set up'],
  ['renew_token', 'Swap the token for a new one with the same access and lifetime before it expires, at most once a day and never past 30 days after you granted it; the old one stops 10 minutes later. Owners and admins are emailed.', 'read'],
  ['get_network_fallback', 'Whether the Network fallback is on for a site, its network and disclosure sentence, and what your partner type allows.', 'read'],
  ['set_network_fallback', 'Turn the Network fallback off or on (both at once; on only once you confirmed its disclosure sentence, with an Undo for owners), when you ask it to.', 'set up'],
  ['request_network_fallback', 'Ask FinderlyFix to turn the Network fallback on (partner type “other”: it checks network fit first), ask to be told when a neutral network exists (warranty, insurer and membership partners), or cancel either.', 'set up'],
  ['get_widget_usage_limits', 'Your widget usage limits for a site (leads this hour and today, AI diagnoses and live conversations this hour): how much is used and the limit.', 'read'],
  ['request_widget_limit_increase', 'Ask FinderlyFix for higher widget usage limits on a site, or withdraw the waiting request. Only with a token you (an owner or admin) created.', 'set up'],
  ['create_site', 'Create a site key pair (live and test).', 'set up'],
  ['list_hosted_pages', 'Your FinderlyFix-hosted widget pages (finderlyfix.com/p/…; up to 3): each one’s address, and whether it is live and indexed.', 'read'],
  ['get_hosted_page', 'One hosted page by its slug (or your first page): its address, and whether it is live and indexed.', 'read'],
  ['set_hosted_page', 'Create a hosted page or change one by its slug: headline, intro, which live site it shows, on or off. It uses your published widget settings for its look.', 'set up'],
  ['get_hosted_page_profile', 'A hosted page’s public profile: phone, service area, trades, services, hours, licence and insurance, about, FAQ, calls to action, and how complete it is.', 'read'],
  ['set_hosted_page_profile', 'Change a hosted page’s public profile text: phone, service area, trades, services, hours, licence and insurance, about, FAQ and calls to action. Photos, the logo and testimonials are added by a person in the portal.', 'set up'],
  ['list_pending_changes', 'Agent changes: which are live (and undoable until when), which were undone, and the invitations waiting for approval.', 'read'],
  ['list_destinations', 'Where leads go: each destination’s kind, a masked address, status and last delivery (never a secret).', 'read'],
  ['send_destination_test_event', 'Send a test event (no homeowner data) to an existing destination.', 'set up'],
  ['get_routing', 'Read the routing profile and rules; flags rules that name contractors who are not active.', 'read'],
  ['set_routing_profile', 'Strategy, response windows, cascade depth and urgency definitions (escalation contacts: request_escalation_contacts).', 'set up'],
  ['set_routing_rules', 'Replace the ordered rules that pin contractors to trades, urgencies or ZIP prefixes.', 'set up'],
  ['update_site', 'Rename a site, or restrict its key to the websites on its list.', 'set up'],
  ['list_site_origins', 'The websites on a site’s list: verified, last seen, blocked.', 'read'],
  ['set_site_origin_status', 'Block, unblock or remove a website on a site’s list.', 'set up'],
  ['update_organisation', 'Change your organisation’s name or partner type (media retention stays with you).', 'set up'],
  ['update_contractor', 'Change a contractor’s coverage, capacity, hours or contact details; it can mark one do-not-contact but never clear it.', 'set up'],
  ['list_team', 'Who is on your team and their roles (read-only: roles and removal stay with you; invite_team_member invites).', 'read'],
  ['get_dashboard_funnel', 'Counts for a period, for every site or one: widget loads, diagnoses, leads and outcomes (no lead details).', 'read'],
  ['list_audit_log', 'What changed and who changed it (no homeowner contact details).', 'read'],
  ['send_test_lead', 'File one test lead through the real widget intake with the test key.', 'set up'],
  ['get_test_lead_status', 'The latest test leads and their deliveries.', 'read'],
  ['pause_widget', 'Stop the widget in one call when you want to pause it or stop using FinderlyFix: pauses the site keys, turns hosted pages off and sets routing to do-it-yourself only. Nothing is deleted; it says what is left.', 'set up'],
  ['resume_widget', 'Turn paused site keys back on.', 'set up'],
  ['revoke_my_access', 'The agent ends its own token (and its renewals) when you are done. You are emailed. Closing your organisation stays with you.', 'read'],
];

export function AgentDocs() {
  const apiBase = mcpUrl().replace(/\/partner\/mcp$/, '');
  const signedIn = useOptionalSession()?.status === 'ready';
  return (
    <main className="shared-dashboard legal-page docs-page" id="main">
      <div className="eyebrow">FinderlyFix Partners · Docs</div>
      <h1 id="page-title" tabIndex={-1}>
        Set up FinderlyFix with an AI agent
      </h1>
      <p className="lede">
        An AI coding agent working in your website’s repository can do the whole widget setup: Claude Code, Codex, VS Code, Cursor or any
        other MCP client. Anything that is a legal act or shows homeowner data stays with a person, in the portal.
      </p>
      <AgentPromptCard commands />

      <h2 id="no-account">No account yet? Let your agent sign up</h2>
      <p>
        The prompt above points your agent at <a href={agentSetupUrl()}><code>{AGENT_SETUP_PATH}</code></a>, plain instructions written for
        agents. It asks you a few questions, proves it controls your website, creates the account, connects to FinderlyFix with the token it
        gets back (no browser sign-in), installs the widget and sends a test lead.
      </p>
      <ul>
        <li>
          <strong>Who authorises it.</strong> The agent names a person authorised to bind your organisation (name, role and work email). That
          person must read the Partner Terms and give the agent an explicit OK; the agent accepts the Terms for your organisation on their
          authority, and records their words.
        </li>
        <li>
          <strong>Proving the website.</strong> A file at <code>/.well-known/finderly-site-verification.txt</code> (for example deployed with{' '}
          <code>npx wrangler deploy</code> to Cloudflare, or pushed to a Git-connected host) or a DNS TXT record at <code>_finderly-challenge.&lt;host&gt;</code> (for example through
          the Cloudflare API). Shared hosts such as <code>*.pages.dev</code>, <code>*.vercel.app</code>, <code>*.github.io</code> and{' '}
          <code>*.netlify.app</code> are fine; the full host is what is proven, and a host can have one unclaimed account at a time.
        </li>
        <li>
          <strong>Claiming.</strong> We email the named person at once. They claim the account by signing in to the portal with that email and
          confirming. Until then the account works fully; if nobody claims it within 14 days, its widget keys stop working until someone does.
          Nothing is deleted.
        </li>
        <li>
          <strong>No website?</strong> An agent can’t sign you up without one to prove. <a href="/sign-in">Sign in</a> with your email instead
          and choose “A page from us”: we host your widget at <code>finderlyfix.com/p/your-name</code>, with a link and QR code. A connected
          agent can then change it for you.
        </li>
        <li>
          <strong>Didn’t authorise it?</strong> The email has a “This wasn’t us” link: it switches the account’s widget keys off, revokes its AI
          agent access and tells FinderlyFix. Nothing is deleted.
        </li>
      </ul>
      <p className="small muted">
        The API calls are <code>POST {apiBase}/partner/signup/challenge</code> and <code>POST {apiBase}/partner/signup/complete</code>; both are
        in the <a href={openApiUrl()}>OpenAPI document</a>, and <a href={agentSetupUrl()}>{AGENT_SETUP_PATH}</a> has the exact requests.
      </p>

      <h2 id="connect">Already a partner? Connect your agent</h2>
      <p>
        With a FinderlyFix account, connect the agent with a browser sign-in: nothing secret is copied. No agent? <a href="/sign-in">Create
        your account</a> with a work email and a six-digit code.
      </p>
      <AgentPicker />
      <p className="small muted">
        The sign-in steps below are the same for every agent that signs in through the browser; they are written for Claude Code.
      </p>
      <ol>
        <li>
          Run the command above in a terminal at your website repo’s root (it is also on <strong>AI agent access</strong> in the portal,
          and under “Set up with Claude Code” on the first setup screen and the Install step). It has no password or token in it.
          <p className="small">
            <InteractiveTerminalNote />
          </p>
        </li>
        <li>
          Claude Code opens your browser at FinderlyFix. Sign in as usual. The next screen names the app asking, the organisation, and where
          your browser returns (for Claude Code, <code>localhost</code> on your computer).
        </li>
        <li>
          Choose what it may do (<strong>Read and set up</strong> or <strong>Read only</strong>), whether it covers all sites or one, and for
          how long (1, 7 or 30 days), then <strong>Allow</strong>. Owners, admins and developers can approve, once your organisation has
          accepted the current Partner Terms (an owner or admin does it in the portal under <strong>Organisation → Partner Terms → Review and accept</strong>).
        </li>
        <li>Back in the terminal, Claude Code starts on the setup.</li>
      </ol>
      <p className="small muted">
        Run it in a terminal, not inside a Claude Code session that is already open: a server added mid-session is loaded the next time Claude
        Code starts. If the browser doesn’t open, run <code>/mcp</code> in Claude Code and authenticate <code>finderlyfix</code>. The sign-in
        is listed on AI agent access, where you can revoke it; Claude Code renews its short-lived access by itself until the time you chose.
      </p>

      <h3>Scripts, CI and agents without browser sign-in: an API key</h3>
      <p>
        On the same card choose the <strong>Use an API key</strong> tab, pick what it may do, when it expires and which sites, and copy the
        command (this too needs the current Partner Terms accepted). The token inside is shown once:
      </p>
      <CodeBlock
        caption="Terminal, at your website repo’s root"
        code={`claude mcp add --transport http finderlyfix ${mcpUrl()} \\\n  --header "Authorization: Bearer fxa_…" \\\n  && claude "${SETUP_PROMPT}"`}
      />

      <h2>What the agent does</h2>
      <ul>
        {AGENT_WILL.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
      <p>
        It will ask you which page the widget goes on, your trades and service area, your contractors, and where leads should go, and it should
        not invent contact details. When it is done it reports what it changed and what is left for you. The widget checks happen against your
        deployed site, so the agent will ask you to deploy before it verifies.
      </p>

      <h2>What only a person can do</h2>
      <ul>
        {HUMAN_ONLY.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
      <p>
        The server enforces this whatever the agent sends: these calls are refused to agent tokens and the refusal names the portal page where
        a person does it. The list is short on purpose: no law requires a person for any other setup step, so the agent does the rest.
      </p>

      <h2>What owners are told about at once, with an Undo</h2>
      <ul>
        {AFTER_A_WINDOW.map((g) => (
          <li key={g.short}>{g.full}</li>
        ))}
      </ul>
      <p>
        Each of these opens a new path for homeowners’ contact details, so owners and admins hear about it right away: {WINDOW_NOTE} A webhook’s signing secret is never shown
        to the agent; an owner or admin reveals one by rotating it on Destinations. A contractor the agent adds or imports, or whose phone or
        email it changes, is saved <strong>inactive</strong> until it is switched on this way (the contact-rules confirmation is still required).
      </p>
      <p>
        Owners and admins get an email when an agent token is created, when an agent makes one of these changes (with an Undo link) or asks for an invitation (with Approve and Reject links),
        when an agent switches a destination or contractor off or on or changes organisation details or the hosted page, when an agent accepts an
        updated version of the Partner Terms, and every time an
        agent publishes widget settings (with the old and new text, and any consent, capture, routing, recording or emergency-screen change).
        If a token keeps trying things only a person may do, they are told at once, and a daily summary lists the day’s refused attempts.
      </p>

      <h2>Tools</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">Tool</th>
              <th scope="col">What it does</th>
              <th scope="col">Needs</th>
            </tr>
          </thead>
          <tbody>
            {DOCUMENTED_TOOLS.map(([name, what, scope]) => (
              <tr key={name}>
                <td>
                  <code>{name}</code>
                </td>
                <td>{what}</td>
                <td>{scope}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="small muted">A read-only token only sees the read tools.</p>
      <p>
        Your organisation can have up to 3 FinderlyFix-hosted widget pages, e.g. one per brand, service area or campaign; each gets its own
        link and QR code.
      </p>
      <p className="small muted">
        Routing rules only offer leads to active contractors, and only on a site whose routing mode files leads; the agent can set the routing
        mode once you tell it where leads should go. A rule that names an inactive contractor has no effect for that contractor until it is
        switched on. Switching contractors on and changing the escalation contacts, which are copied on lead notices, take effect at once;
        owners and admins are emailed with a one-click Undo.
      </p>

      <h2>Security</h2>
      <ul>
        <li>A token belongs to one organisation and, if you choose, to one site. It can never read your leads’ homeowner details, your team, secrets or billing.</li>
        <li>
          It acts with the role of the person who created it. If that person is demoted, the token loses the access with them; if they are
          removed, their tokens are revoked.
        </li>
        <li>It expires (at most 30 days) and you can revoke it at any time on AI agent access. FinderlyFix stores only a hash of it.</li>
        <li>
          Every change an agent makes is in the Audit log with the token’s name and the person who created it. Refused attempts at human-only
          actions are logged too.
        </li>
        <li>The same validation applies as in the portal: colour contrast, website origin rules, URL checks. Nothing is relaxed for an agent.</li>
        <li>Agent tokens have their own rate limits.</li>
        <li>
          A browser sign-in gives the agent access that lasts an hour and renews itself until the time you chose. It works only on the MCP
          server, not on the rest of the API, and FinderlyFix stores only hashes of it. If a renewal is ever replayed, the sign-in is revoked.
        </li>
        <li>Treat a token like a password: don’t commit it, paste it into chat or share it in a ticket.</li>
      </ul>

      <h2>Other MCP clients, or a shared config</h2>
      <p>
        MCP clients that support sign-in (the MCP authorization spec) need only the server’s address; this file has nothing secret in it and
        is safe to commit:
      </p>
      <CodeBlock caption=".mcp.json" code={mcpJsonOAuthConfig()} />
      <p>
        For a client that cannot sign in, keep a token in an environment variable (<code>{TOKEN_ENV_VAR}</code>). Claude Code reads{' '}
        <code>{'${VAR}'}</code> from a project’s <code>.mcp.json</code>; check how your client handles variables before committing its config.
      </p>
      <CodeBlock caption=".mcp.json (token)" code={mcpJsonConfig()} />

      <h2 id="repo-config">Commit agent config to your repo (optional)</h2>
      <p>
        For teams that keep agent setup in version control: these files carry the same playbook the MCP server gives the agent, generated
        from one source so they stay in step. Copy them one by one or download <code>{BUNDLE_ZIP_NAME}</code> and unzip at the repository
        root.
      </p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">File</th>
              <th scope="col">What it is</th>
            </tr>
          </thead>
          <tbody>
            {BUNDLE_FILES.map(([path, what]) => (
              <tr key={path}>
                <td>
                  <code>{path}</code>
                </td>
                <td>{what}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="notice warn" role="note">
        <span>
          <strong>Never commit the token.</strong> Each developer sets <code>{TOKEN_ENV_VAR}</code> in their own shell or secret manager, never
          in the repository, a committed <code>.env</code>, a ticket or chat.
        </span>
      </div>
      <CodeBlock caption="Your shell, not the repo" code={`export ${TOKEN_ENV_VAR}="fxa_…"`} />
      {signedIn ? (
        <RepoAgentConfig />
      ) : (
        <p className="small muted">
          <a href="/sign-in?next=%2Fdocs%2Fai-setup">Sign in to the partner portal</a> and come back to this page to copy or download the files.
        </p>
      )}
      <p className="small muted">
        Claude Code asks once to approve a project MCP server from <code>.mcp.json</code>, and <code>claude mcp list</code> warns if the
        variable is not set. Claude Code reads <code>AGENTS.md</code> only when the repository has no <code>CLAUDE.md</code>; the skill
        covers Claude Code either way, or add <code>@AGENTS.md</code> to your <code>CLAUDE.md</code>.
      </p>

      <h2>Without MCP</h2>
      <p>
        A token (not a browser sign-in) also works as a bearer token on the partner API for the routes an agent may use (sites, origins, verification, widget
        settings, contractors, routing, and the <code>/partner/agent/*</code> helpers).
      </p>
      <CodeBlock caption="Check the token" code={curlExample()} />
      <p>
        The OpenAPI 3.1 description of exactly those routes is at{' '}
        <a href={openApiUrl()}>
          <code>/partner/openapi.json</code>
        </a>{' '}
        (public, no token needed). It is generated from the server’s own allowlist and request schemas, and each operation says whether it
        needs a <strong>Set up</strong> token (the <code>config</code> scope) or works with a read-only one.
      </p>

      <h2>If something is refused</h2>
      <div className="table-wrap">
        <table>
          <tbody>
            <tr>
              <td><code>AGENT_HUMAN_ONLY</code></td>
              <td>A person does this in the portal; the response has the link.</td>
            </tr>
            <tr>
              <td><code>AGENT_DELAYED_CHANGE</code></td>
              <td>Not a refusal: the agent makes this change with another tool (the response names it); it applies at once, with an Undo for owners.</td>
            </tr>
            <tr>
              <td><code>AGENT_HUMAN_ONLY_FIELD</code></td>
              <td>The change touches a field only a person may change: {listInWords(HUMAN_ONLY_FIELD_GROUPS)}.</td>
            </tr>
            <tr>
              <td><code>AGENT_HUMAN_ONLY_PUBLISH</code></td>
              <td>The draft has a change a person saved but has not published. Publish it from Widget settings.</td>
            </tr>
            <tr>
              <td><code>AGENT_SCOPE_REQUIRED</code></td>
              <td>The token is read-only. Create one that can set up.</td>
            </tr>
            <tr>
              <td><code>AGENT_TOKEN_INVALID</code></td>
              <td>The token is unknown, expired or revoked. Sign in again (<code>/mcp</code>) or create a new command.</td>
            </tr>
            <tr>
              <td><code>CONTRAST_TOO_LOW</code></td>
              <td>A colour pair fails the contrast rules. Run check_branding.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="small">
        A save that sends one of those fields unchanged is accepted, with an <code>AGENT_HUMAN_ONLY_UNCHANGED</code> warning naming it. A save
        identical to the latest version creates no new version; it answers <code>unchanged: true</code> with that version.
      </p>
      <p className="small muted">Questions: partners@finderlyfix.com</p>
    </main>
  );
}
